import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Users, BookOpen, Layout, Calendar, Briefcase, Handshake, Heart, Infinity } from 'lucide-react';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../../context/LanguageContext';
import './About.css';

import aboutInfoImg from '../../assets/AboutPictures/aboutImg1.jpg';
import aboutVideo from '../../assets/AboutVideos/aboutVideo1.mp4';
import ccisoBadge from '../../assets/BadgePictures/CCISO_3DB19ABBA7E9.png';
import ecsaBadge from '../../assets/BadgePictures/ECSAExambadge.png';

const About = () => {
    const { lang } = useLanguage();

    const { ref, inView } = useInView({
        triggerOnce: true,
        threshold: 0.3,
    });

    const content = {
        en: {
            mainTitle: "Level 9 Coaching",
            stat1: "Instructors",
            stat2: "Courses",
            stat3: "New Courses Coming Soon",

            // Section 2
            infoTitle: "Company Information",
            compNameLabel: "Company Name",
            addressLabel: "Address",
            addressInfo: "27F Shinjuku i Land Tower, 6-5-1 Nishishinjuku, Shinjuku-Ku Tokyo, Japan 163-1327",
            phoneLabel: "Phone Number",
            emailLabel: "Email",

            //Section 3
            sec3Title: "Top level education",
            sec3Subtitle: "We've created a unique, more personal approach to learning.",
            sec3Desc: "With a vast instructor pool of knowledge you will be taught how to grasp the basic and advanced side of IT. This isn't your basic teaching you will learn a variety of skills to set you on the perfect path of higher learning.",
            blockTag: "What to expect",
            blockTag2: "What we offer",
            blockTag3: "What we do",
            block1Text: "To learn new skills",
            block2Text: "Courses",
            block3Text: "Top level learning",
            s3Stat1: "company established",
            s3Stat2: "classes offered",
            s3Stat3: "years of experience",
            s3Stat4: "Happy clients",

            //Section 4
            enrollTitle: "Enroll today",
            enrollBtn: "View Our Courses"
        },
        jp: {
            mainTitle: "Level 9 コーチング",
            stat1: "講師",
            stat2: "コース数",
            stat3: "新コース近日公開",

            // Section 2
            infoTitle: "企業情報",
            compNameLabel: "会社名",
            addressLabel: "所在地",
            addressInfo: "〒163-1327 東京都新宿区西新宿6丁目5-1 新宿アイランドタワー 27F",
            phoneLabel: "電話番号",
            emailLabel: "メール",

            //Section 3
            sec3Title: "トップレベルの教育",
            sec3Subtitle: "一人ひとりに最適化された、実践的な学習アプローチ",
            sec3Desc: "実務経験豊富な講師陣が、ITの基礎から応用まで体系的に指導します。単なる基礎習得にとどまらず、現場で求められる多様なスキルを身につけ、キャリアアップにつながる学習環境を提供します。",
            blockTag: "ご利用の流れ",
            blockTag2: "サービス内容",
            blockTag3: "事業内容",
            block1Text: "新しいスキルを身につける",
            block2Text: "コース内容",
            block3Text: "トップレベルの学び",
            s3Stat1: "会社設立",
            s3Stat2: "コース数",
            s3Stat3: "経験年数",
            s3Stat4: "受講者満足度",

            //Section 4
            enrollTitle: "今すぐ申し込む",
            enrollBtn: "コース一覧を見る"
        }
    };

    const t = content[lang];

    const subStats = [
        { icon: <Calendar size={64} strokeWidth={1.5} />, number: 2021, label: t.s3Stat1, isInfinity: false },
        { icon: <Briefcase size={64} strokeWidth={1.5} />, number: "∞", label: t.s3Stat2, isInfinity: true },
        { icon: <Handshake size={64} strokeWidth={1.5} />, number: 10, label: t.s3Stat3, isInfinity: false },
        { icon: <Heart size={64} strokeWidth={1.5} />, number: 1000, label: t.s3Stat4, isInfinity: false, suffix: "+" }
    ];

    const stats = [
        {
            number: 12,
            label: t.stat1,
            icon: <Users size={40} />,
            suffix: ""
        },
        {
            number: 100,
            label: t.stat2,
            icon: <BookOpen size={40} />,
            suffix: ""
        },
        {
            number: 25,
            label: t.stat3,
            icon: <Layout size={40} />,
            suffix: "+"
        }
    ];

    return (
        <div className="about-page-wrapper">
            {/* SECTION 1: Stats Header */}
            <section className="about-stats-section py-5" ref={ref}>
                <Container>
                    <div className="text-center mb-5">
                        <h2 className="gs-main-title">{t.mainTitle}</h2>
                        <div className="small-red-line mx-auto"></div>
                    </div>

                    <Row className="justify-content-center g-4">
                        {stats.map((stat, index) => (
                            <Col lg={4} md={6} key={index}>
                                <div className="about-stat-item text-center">
                                    <div className="about-stat-icon-box mx-auto mb-4">
                                        {stat.icon}
                                    </div>

                                    <div className="about-stat-number">
                                        {inView ? (
                                            <CountUp end={stat.number} duration={2.5} />
                                        ) : (
                                            "0"
                                        )}
                                        {stat.suffix}
                                    </div>

                                    <h4 className="about-stat-label mt-2">{stat.label}</h4>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            {/* Section 1 ------------------------------------------------------------------------------*/}

            {/* Section 2 ------------------------------------------------------------------------------*/}
            <section className="about-info-section py-5 bg-white text-dark">
                <Container>
                    <Row className="align-items-center g-5">
                        <Col lg={6}>
                            <div className="about-info-img-wrapper shadow-lg">
                                <img src={aboutInfoImg} alt="Level 9 Office" className="img-fluid about-info-img" />
                            </div>
                        </Col>


                        <Col lg={6}>
                            <div className="about-info-content ps-lg-4">
                                <h2 className="info-main-title mb-4">{t.infoTitle}</h2>

                                <div className="info-detail-item mb-4">
                                    <label className="info-label text-muted">{t.compNameLabel}</label>
                                    <p className="info-value fw-bold fs-4">Level 9 Coaching</p>
                                </div>

                                <div className="info-detail-item mb-4">
                                    <label className="info-label text-muted">{t.addressLabel}</label>
                                    <p className="info-value">
                                        {t.addressInfo}
                                    </p>
                                </div>

                                <div className="info-detail-item mb-4">
                                    <label className="info-label text-muted">{t.phoneLabel}</label>
                                    <p className="info-value mb-1">03-6773-4452</p>
                                </div>

                                <div className="info-detail-item">
                                    <label className="info-label text-muted">{t.emailLabel}</label>
                                    <p className="info-value mb-1">learn@level9.co.jp</p>
                                </div>

                                <div className="about-badges-wrapper">
                                    <p className="info-label text-muted mb-2 text-uppercase fw-bold" style={{ fontSize: '12px', letterSpacing: '1px' }}>
                                        {lang === 'en' ? 'Instructor Expertise' : '講師の保有資格'}
                                    </p>

                                    <p className="text-muted small mb-3" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                                        {lang === 'en'
                                            ? "Our programs are led by experts holding world-class certifications from Level 9 Corporation."
                                            : "当社のプログラムは、Level 9 Corporationによる世界クラスの認定資格を持つエキスパートによって指導されています。"}
                                    </p>

                                    <div className="d-flex gap-3">
                                        <div className="about-badge-frame">
                                            <img src={ccisoBadge} alt="C|CISO Certified" className="about-cert-img" />
                                        </div>
                                        <div className="about-badge-frame">
                                            <img src={ecsaBadge} alt="E|CSA Certified" className="about-cert-img" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>

                <div className="container mt-5">
                    <hr className="about-section-divider" />
                </div>
            </section>
            {/* Section 2 ------------------------------------------------------------------------------*/}

            {/* Section 3 (Education & Info Blocks) -----------------------------------------------------*/}

            <section className="about-edu-section bg-white">
                <Container>
                    {/* Top Text Row */}
                    <Row className="mb-5 align-items-center">
                        <Col lg={6}>
                            <div className="edu-left-content">
                                <h2 className="edu-title">{t.sec3Title}</h2>
                                <p className="edu-subtitle">{t.sec3Subtitle}</p>
                            </div>
                        </Col>
                        <Col lg={6}>
                            <p className="edu-description text-muted">
                                {t.sec3Desc}
                            </p>
                        </Col>
                    </Row>

                    {/* 3 Blocks Row */}
                    <Row className="g-0">
                        <Col md={4}>
                            <div className="edu-block block-orange">
                                <span className="block-tag">{t.blockTag}</span>
                                <h3 className="block-main-text">{t.block1Text}</h3>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="edu-block block-dark-blue">
                                <span className="block-tag">{t.blockTag2}</span>
                                <h3 className="block-main-text">{t.block2Text}</h3>
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="edu-block block-gray">
                                <span className="block-tag">{t.blockTag3}</span>
                                <h3 className="block-main-text">{t.block3Text}</h3>
                            </div>
                        </Col>
                    </Row>

                    <div className="about-sub-stats mt-5 pt-5">
                        <Row className="g-4 justify-content-center">
                            {subStats.map((item, index) => (
                                <Col key={index} lg={3} md={6} className="text-center">
                                    <div className="sub-stat-item">
                                        <div className="sub-stat-icon mb-3">
                                            {item.icon}
                                        </div>
                                        <div className="sub-stat-number">
                                            {item.isInfinity ? (
                                                <span className="infinity-symbol">∞</span>
                                            ) : (
                                                inView && <CountUp end={item.number} duration={3} />
                                            )}
                                            {item.suffix}
                                        </div>
                                        <p className="sub-stat-label">{item.label}</p>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </div>
                </Container>
            </section>

            {/* Section 3 ------------------------------------------------------------------------------*/}

            {/* Section 4  (Video Enroll Section)--------------------------------------------------------*/}

            <section className="about-enroll-section">
                {/* Video Background */}
                <video autoPlay loop muted playsInline className="enroll-video-bg">
                    <source src={aboutVideo} type="video/mp4" />
                </video>

                {/* Overlay (Zatamnjenje da bi se tekst video) */}
                <div className="enroll-overlay d-flex align-items-center justify-content-center">
                    <Container className="text-center">
                        <h2 className="enroll-main-text mb-4 animate-text">
                            {t.enrollTitle}
                        </h2>
                        <Link to="/courses" className="btn-lava-enroll">
                            {t.enrollBtn}
                        </Link>
                    </Container>
                </div>
            </section>

            {/* Section 4 ------------------------------------------------------------------------------*/}

        </div>
    );
};

export default About;